// QuizHistory.tsx - Pure component with no internal state
import React, { useState, useMemo } from "react";
import {
  Award,
  Calendar,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Users,
  GraduationCap,
  CheckCircle2,
  XCircle,
  Filter,
} from "lucide-react";
import { StudentWithGrade } from "./types";

interface QuizHistoryProps {
  history: StudentWithGrade[];
  topics: { id: string; title: string; icon?: string }[];
  students: { id: string; name: string; grade: string }[];
  onHistoryChange: (history: StudentWithGrade[]) => void;
  className?: string;
}

export default function QuizHistory({
  history,
  topics,
  students,
  onHistoryChange,
  className = "",
}: QuizHistoryProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<'all' | 'passed' | 'failed'>('all');
  const [selectedStudent, setSelectedStudent] = useState<string>("all");

  const filteredHistory = useMemo(() => {
    let filtered = [...history];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(h =>
        h.studentName.toLowerCase().includes(term) ||
        topics.find(t => t.id === h.topicId)?.title?.toLowerCase().includes(term)
      );
    }

    if (filterType === 'passed') {
      filtered = filtered.filter(h => h.passed);
    } else if (filterType === 'failed') {
      filtered = filtered.filter(h => !h.passed);
    }

    if (selectedStudent !== 'all') {
      filtered = filtered.filter(h => h.studentId === selectedStudent);
    }

    return filtered.sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
  }, [history, searchTerm, filterType, selectedStudent, topics]);

  const stats = {
    total: history.length,
    passed: history.filter(h => h.passed).length,
    failed: history.filter(h => !h.passed).length,
  };

  const handleDelete = (record: StudentWithGrade) => {
    if (confirm(`Delete ${record.studentName}'s result for "${topics.find(t => t.id === record.topicId)?.title}"?`)) {
      onHistoryChange(history.filter(h => !(h.studentId === record.studentId && h.topicId === record.topicId)));
    }
  };

  if (history.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-4xl mb-4">📊</div>
        <h3 className="text-lg font-semibold text-gray-600">No Quiz History</h3>
        <p className="text-gray-400">Complete a quiz to see results here</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${className}`}>
      {/* Stats */}
      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex gap-6">
          <Stat label="Total" value={stats.total} color="blue" />
          <Stat label="Passed" value={stats.passed} color="green" />
          <Stat label="Failed" value={stats.failed} color="red" />
          <Stat label="Pass Rate" value={`${Math.round((stats.passed / stats.total) * 100)}%`} color="purple" />
        </div>
      </div>

      {/* Filters */}
      <div className="p-3 border-b border-gray-100 flex flex-wrap gap-2">
        <div className="flex-1 min-w-[150px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as any)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
        >
          <option value="all">All Results</option>
          <option value="passed">✅ Passed</option>
          <option value="failed">❌ Failed</option>
        </select>
        <select
          value={selectedStudent}
          onChange={(e) => setSelectedStudent(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
        >
          <option value="all">All Students</option>
          {students.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Topic</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Date</th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredHistory.map((record) => {
              const topic = topics.find(t => t.id === record.topicId);
              return (
                <tr key={`${record.studentId}-${record.topicId}`} className="hover:bg-gray-50 transition">
                  <td className="px-3 py-2 font-medium text-gray-800">{record.studentName}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {topic?.icon && <span>{topic.icon}</span>}
                      <span className="text-gray-600">{topic?.title || record.topicId}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-bold">{record.score}%</td>
                  <td className="px-3 py-2">
                    {record.passed ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle2 className="w-3 h-3" />
                        Passed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                        <XCircle className="w-3 h-3" />
                        Failed
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400 hidden md:table-cell">
                    {new Date(record.completedAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => handleDelete(record)}
                      className="p-1 text-red-400 hover:bg-red-50 rounded-lg transition"
                      title="Delete"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
        Showing {filteredHistory.length} of {history.length} records
      </div>
    </div>
  );
}

const Stat = ({ label, value, color }: any) => (
  <div>
    <p className="text-xs text-gray-400">{label}</p>
    <p className={`text-lg font-bold text-${color}-600`}>{value}</p>
  </div>
);